import React, { useState, createContext, useContext, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import debounce from 'lodash/debounce';
import Dispatcher from '@dealroadshow/json-rpc-dispatcher';
import ErrorCodeHelper from '@finsight/error-codes';
import filtersSelectors from '@/finsight/application/actions/trace/filters/selectors';
import TraceRepository from '@/finsight/infrastructure/repository/TraceRepository';
import { useDIContext } from '@/Framework/DI/DIContext';
import { ITradesCollection } from '@/finsight/domain/vo/Trace/ITraceData';
import { useTraceAssetClassesContext } from '@/finsight/application/Trace/TraceAssetClassesContext';
import { useFiltersContext } from '@/finsight/application/FiltersContext';
import { initialTraceData } from '@/finsight/application/Trace/constants';
import { useSessionContext } from '@/users/application/Session/SessionContext';
import useGetTracePayload from '@/finsight/application/Trace/useGetTracePayload';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';
import { IMarketTradesPayload } from '@/finsight/domain/vo/Trace/IMarketTradesPayload';

const stateSelector = (state) => ({
  filter: filtersSelectors.getFiltersPayload(state),
});

export const useTraceTable = () => {
  const { container } = useDIContext();
  const traceRepository = container.get(TraceRepository);
  const [traceData, setTraceData] = useState<ITradesCollection>(initialTraceData.trades);
  const [isTraceFetching, setIsTraceFetching] = useState(false);
  const { filter } = useSelector(stateSelector);
  const { currentAssetClass } = useTraceAssetClassesContext();
  const { params } = useFiltersContext();
  const { logout } = useSessionContext();
  const { isPro } = useUserPlanContext();

  const { getPayload } = useGetTracePayload();

  useEffect(() => {
    getTrades(getPayload({}));
  }, [params.sortBy, params.sortOrder, params.page, params.perPage, currentAssetClass, filter]);

  const getTrades = async (payload: Partial<IMarketTradesPayload>) => {
    if (!isPro) return;

    setIsTraceFetching(true);
    try {
      const response = await traceRepository.getMarketTrades(payload);
      setTraceData(response.trades);
    } catch (e) {
      if (e.code === ErrorCodeHelper.getCodeByName('FINSIGHT_CONTACT_SUBSCRIPTION_REQUIRED')) {
        logout();
      }
      if (Dispatcher.isAbortError(e)) {
        return;
      }
    }

    setIsTraceFetching(false);
  };

  const debouncedGetTrades = useCallback(debounce(getTrades, 500), []);

  return {
    isTraceFetching,
    getTrades,
    setTraceData,
    traceData,
    debouncedGetTrades,
  };
};

type TraceTableContextType = ReturnType<typeof useTraceTable>;

export const TraceTableContext = createContext<TraceTableContextType>(null);

export const useTraceTableContext = () => {
  const context = useContext(TraceTableContext);
  if (!context) {
    throw new Error('useTraceTableContext must be used within the TraceTableContext');
  }
  return context;
};

interface IProps {
  children: React.ReactNode,
}

const TraceTableContextProvider = ({ children }: IProps) => (
  <TraceTableContext.Provider value={ useTraceTable() }>
    { children }
  </TraceTableContext.Provider>
);

export default TraceTableContextProvider;
